import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button, CheckBox } from "components/atoms";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  TextInput,
  DatePickerInput,
  MessageTextAreaInput,
  MultiChoiceCheckboxGroup,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import {
  ApplicationFormValues,
  employmentStatusOptions,
  courseReasonOptions,
  FileWithType,
} from "types";

interface AppEmploymentSectionProps {
  currentSubStage?: string;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["employmentInformation"]),
  employment1Details: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
  ]),
  employment2Details: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
  ]),
  employment3Details: new Set([
    "companyName",
    "positionAndDuties",
    "dateCommenced",
    "dateEnded",
  ]),
};

const AppEmploymentSection: React.FC<AppEmploymentSectionProps> = ({
  currentSubStage = "emOverview",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ApplicationFormValues>();

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    employment1Reference?: { id: string; fileName: string };
    employment2Reference?: { id: string; fileName: string };
    employment3Reference?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const employmentTypes = [
    "EMPLOYMENT_1_REFERENCE",
    "EMPLOYMENT_2_REFERENCE",
    "EMPLOYMENT_3_REFERENCE",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(employmentTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "employment1reference",
        label: "Employment 1 Reference",
        description: "Reference letter from your latest employer",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!values.employment1CompanyName,
        uploaded: !!uploadedDocuments.employment1Reference,
        stage: "employment1",
      },
      {
        id: "employment2reference",
        label: "Employment 2 Reference",
        description: "Reference letter from your second employer",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!values.employment2CompanyName,
        uploaded: !!uploadedDocuments.employment2Reference,
        stage: "employment2",
      },
      {
        id: "employment3reference",
        label: "Employment 3 Reference",
        description: "Reference letter from your third employer",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: !!values.employment3CompanyName,
        uploaded: !!uploadedDocuments.employment3Reference,
        stage: "employment3",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(employmentTypes);

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const getFieldError = (fieldName: string): string | undefined => {
    const error = errors[fieldName as keyof ApplicationFormValues];
    return typeof error === "string" ? error : undefined;
  };

  const getFieldTouched = (fieldName: string): boolean | undefined => {
    const touch = touched[fieldName as keyof ApplicationFormValues];
    return typeof touch === "boolean" ? touch : undefined;
  };

  const handleCurrentlyWorkingChange = async (
    field: string,
    checked: boolean,
  ) => {
    if (checked) {
      if (field === "employment1IsStillWorking") {
        setFieldValue("employment1IsStillWorking", true);
        setFieldValue("employment1DateEnded", null);
        setFieldValue("employment2IsStillWorking", false);
        setFieldValue("employment3IsStillWorking", false);
      }
    } else {
      setFieldValue(field, false);
    }
  };

  const renderEmploymentOverviewSection = () => (
    <div className="employment-overview-section">
      <div className="form-section">
        <div className="form-group">
          <h4>Employment Overiew</h4>
        </div>

        <div className="form-fields">
          <GeneralSelectInput
            id="currentEmploymentStatus"
            label="Current Employment Status *"
            options={employmentStatusOptions}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.currentEmploymentStatus as string}
            error={errors.currentEmploymentStatus}
            touched={touched.currentEmploymentStatus}
          />
          <MultiChoiceCheckboxGroup
            name="reasonForUndertakingCourse"
            label="Main reason for undertaking course"
            options={courseReasonOptions}
            value={values.reasonForUndertakingCourse as string[]}
            otherValue={values.reasonOtherDetails as string}
            onChange={(values) =>
              setFieldValue("reasonForUndertakingCourse", values)
            }
            onOtherChange={(value) => {
              setFieldValue("reasonOtherDetails", value);
              setFieldTouched("reasonOtherDetails", true);
            }}
            showOther={true}
            otherPlaceholder="Please specify"
            required={true}
          />
        </div>
      </div>
    </div>
  );

  const renderEmploymentSection = (index: number) => {
    const prefix = `employment${index}`;
    const labels = ["Latest", "Second", "Third"];
    const descriptions = [
      "Enter your most recent employment (optional).",
      "Enter your second employment (optional).",
      "Enter your third employment (optional).",
    ];

    return (
      <div className="employment-section">
        <div className="form-section">
          <div className="form-group">
            <h4>{labels[index - 1]} Employment</h4>
            <p>{descriptions[index - 1]}</p>
          </div>

          <div className="form-fields">
            <TextInput
              id={`${prefix}CompanyName`}
              label="Company Name"
              placeholder="Enter company name"
              getFieldProps={() => ({
                value: values[
                  `${prefix}CompanyName` as keyof ApplicationFormValues
                ] as string,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={getFieldError(`${prefix}CompanyName`)}
              touched={getFieldTouched(`${prefix}CompanyName`)}
            />
            <MessageTextAreaInput
              id={`${prefix}PositionAndDuties`}
              label="Position and Duties"
              placeholder="Describe your position and main duties/responsibilities"
              rows="4"
              maxLength={500}
              getFieldProps={() => ({
                value: values[
                  `${prefix}PositionAndDuties` as keyof ApplicationFormValues
                ] as string,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={getFieldError(`${prefix}PositionAndDuties`)}
              touched={getFieldTouched(`${prefix}PositionAndDuties`)}
            />
            <div className="employment-date-container">
              <DatePickerInput
                id={`${prefix}DateCommenced`}
                className={`${prefix}DateCommenced`}
                label="Date Commenced"
                value={
                  values[
                    `${prefix}DateCommenced` as keyof ApplicationFormValues
                  ] as Date | null
                }
                onChange={(date) =>
                  setFieldValue(`${prefix}DateCommenced`, date)
                }
                preset="pastDate"
              />
              <DatePickerInput
                id={`${prefix}DateEnded`}
                className={`${prefix}DateEnded`}
                label="Date Ended"
                value={
                  values[
                    `${prefix}DateEnded` as keyof ApplicationFormValues
                  ] as Date | null
                }
                disabled={
                  values[
                    `${prefix}IsStillWorking` as keyof ApplicationFormValues
                  ] as boolean
                }
                onChange={(date) => setFieldValue(`${prefix}DateEnded`, date)}
                preset="pastDate"
              />
            </div>
            <CheckBox
              label="Currently working here"
              checked={
                values[
                  `${prefix}IsStillWorking` as keyof ApplicationFormValues
                ] as boolean
              }
              disabled={index !== 1}
              onChange={(checked) =>
                handleCurrentlyWorkingChange(`${prefix}IsStillWorking`, checked)
              }
            />

            {documentService.countRequiredDocs(
              getDocumentFields(currentSubStage),
            ) > 0 && (
              <div className="documents-section">
                <div className="upload-header">
                  <Button
                    type="button"
                    text="Upload Work References"
                    onClick={() => setShowDocumentModal(true)}
                    variant="border"
                    width="12rem"
                  />
                  <div className="docs-counter">
                    <span className="uploaded-count">
                      {documentService.countUploadedDocs(
                        getDocumentFields(currentSubStage),
                      )}
                    </span>
                    <span className="separator">/</span>
                    <span className="total-count">
                      {documentService.countRequiredDocs(
                        getDocumentFields(currentSubStage),
                      )}
                    </span>
                    <span className="docs-label">documents</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DocumentUploadModal
          isOpen={showDocumentModal}
          onClose={() => {
            handleError("");
            setShowDocumentModal(false);
          }}
          title="Upload Work References"
          description="Please attach reference letters from current or previous employers:"
          documents={getDocumentFields(currentSubStage)}
          onFileUpload={handleUploadWithRefresh}
          onFileRemove={handleFileRemove}
          validateFile={validateFileSync}
          acceptedFileTypes={getAcceptedFileTypes()}
          onError={handleError}
          errorMessage={errorMessage}
          successMessage={successMessage}
          maxFileSize={getMaxFileSize()}
          stage={stage}
          uploadProgress={uploadProgress}
        />
      </div>
    );
  };

  switch (currentSubStage) {
    case "emOverview":
      return renderEmploymentOverviewSection();
    case "employment1":
      return renderEmploymentSection(1);
    case "employment2":
      return renderEmploymentSection(2);
    case "employment3":
      return renderEmploymentSection(3);
    default:
      return renderEmploymentOverviewSection();
  }
};

export default AppEmploymentSection;
